import { useMemo, useState } from 'react';
const generateEmbedScript = (fileName) => {
    return `<script src="path_to_the_file/${fileName}" strategy="afterInteractive"></script>`;
};
const generateScript = (framework, config, themeData, componentName) => {
    const kebabCaseComponentName = componentName
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replaceAll('.', '-')
        .toLowerCase();
    const formattedComponentName = componentName.replaceAll('.', '');
    const jobPostingScript = `
function render${componentName}() {
  const theme = ${JSON.stringify(themeData, null, 4)};
  const config = ${JSON.stringify(config, (key, value) => {
        if (typeof value === 'function') {
            return `(${value.toString()})`;
        }
        return value;
    }, 4)};
  ${componentName}({elementId:"${kebabCaseComponentName}-widget", config, theme});
}
if (
  document.readyState === "complete" ||
  document.readyState === "interactive"
) {
  render${formattedComponentName}();
} else {
  document.addEventListener("DOMContentLoaded", render${formattedComponentName});
}
  `;
    switch (framework) {
        case 'React':
            return `
import { useEffect } from 'react';

const useView${formattedComponentName} = () => {
  useEffect(() => {

    const script = document.createElement('script');
  
    script.innerHTML = \`${jobPostingScript}\`;
    
    document.body.appendChild(script);
  
    return () => {
      document.body.removeChild(script);
    }
  }, []);
};

export default useView${formattedComponentName};
      `;
        case 'Next.js':
            return `
    "use client";
    import Script from 'next/script';

    const ${formattedComponentName}Component = () => (
      <>
        <div id="${kebabCaseComponentName}-widget"></div>
        <Script id="job-posting" strategy="lazyOnload">
      {\`${jobPostingScript}\`}
        </Script>
      </>
    );

    export default ${formattedComponentName}Component;
      `;
        case 'Angular':
            return `
import { Component, OnInit } from '@angular/core';

@Component({
  selector: 'app-job-posting',
  template: '<div id="${kebabCaseComponentName}-widget"></div>'
})
export class ${formattedComponentName}Component implements OnInit {
  ngOnInit() {
    ${jobPostingScript}
  }
}
      `;
        default:
            return `
<div id="${kebabCaseComponentName}-widget"></div>
<script>
  ${jobPostingScript}
</script>
      `;
    }
};
export const useGeneratedScripts = ({ scriptConfig, themeConfig, setIsScriptGenerated, fileInfo, componentName = 'AiInterviewWidget', }) => {
    const [generatedScripts, setGeneratedScripts] = useState([]);
    const { fileName, fileUrl } = useMemo(() => {
        if (fileInfo) {
            return fileInfo;
        }
        return {
            fileName: 'ai-interview.bundle.js',
            fileUrl: '/ai-interview.bundle.js',
        };
    }, [fileInfo]);
    const handleSubmit = (e) => {
        e.preventDefault();
        const formattedComponentName = componentName
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\b\w/g, (char) => char.toUpperCase());
        const scripts = [
            // AiInterviewDashboardWidget
            {
                title: "Download the scripts and move them to your project's code",
                file: {
                    fileName,
                    fileUrl,
                },
            },
            {
                script: generateEmbedScript(fileName),
                title: `Embed AiInterviewDashboardWidget into the app.`,
                description: 'Copy and paste the following code in your body tag of html code to inject the script.',
                note: 'Replace path_to_the_file with your file path.',
            },
            {
                script: [
                    {
                        framework: 'HTML',
                        script: generateScript('HTML', scriptConfig, themeConfig, `${componentName}.AiInterviewDashboardWidget`),
                    },
                    {
                        framework: 'React',
                        script: generateScript('React', scriptConfig, themeConfig, `${componentName}.AiInterviewDashboardWidget`),
                    },
                    {
                        framework: 'Next.js',
                        script: generateScript('Next.js', scriptConfig, themeConfig, `${componentName}.AiInterviewDashboardWidget`),
                    },
                    {
                        framework: 'Angular',
                        script: generateScript('Angular', scriptConfig, themeConfig, `${componentName}.AiInterviewDashboardWidget`),
                    },
                ],
                title: `Integrate AiInterviewDashboardWidget UI into your framework.`,
                description: `Copy and Paste the following code where you want to launch the ${formattedComponentName} UI`,
                note: 'Make sure main script should available when you execute this code. Update the callback function to set the selected flow ID or perform side effects on flow selection.',
            },
            // AiInterviewEvaluationWidget
            {
                script: generateEmbedScript(fileName),
                title: `Embed AiInterviewEvaluationWidget into the app.`,
                description: 'Copy and paste the following code in your body tag of html code to inject the script.',
                note: 'Replace path_to_the_file with your file path.',
            },
            {
                script: [
                    {
                        framework: 'HTML',
                        script: generateScript('HTML', scriptConfig, themeConfig, `${componentName}.AiInterviewEvaluationWidget`),
                    },
                    {
                        framework: 'React',
                        script: generateScript('React', scriptConfig, themeConfig, `${componentName}.AiInterviewEvaluationWidget`),
                    },
                    {
                        framework: 'Next.js',
                        script: generateScript('Next.js', scriptConfig, themeConfig, `${componentName}.AiInterviewEvaluationWidget`),
                    },
                    {
                        framework: 'Angular',
                        script: generateScript('Angular', scriptConfig, themeConfig, `${componentName}.AiInterviewEvaluationWidget`),
                    },
                ],
                title: `Integrate AiInterviewEvaluationWidget UI into your framework.`,
                description: `Copy and Paste the following code where you want to launch the ${formattedComponentName} UI`,
                note: 'Make sure main script should available when you execute this code. Update the callback function to set the selected flow ID or perform side effects on flow selection.',
            },
            // AiInterviewMeetingWidget
            {
                script: generateEmbedScript(fileName),
                title: `Embed AiInterviewMeetingWidget into the app.`,
                description: 'Copy and paste the following code in your body tag of html code to inject the script.',
                note: 'Replace path_to_the_file with your file path.',
            },
            {
                script: [
                    {
                        framework: 'HTML',
                        script: generateScript('HTML', scriptConfig, themeConfig, `${componentName}.AiInterviewMeetingWidget`),
                    },
                    {
                        framework: 'React',
                        script: generateScript('React', scriptConfig, themeConfig, `${componentName}.AiInterviewMeetingWidget`),
                    },
                    {
                        framework: 'Next.js',
                        script: generateScript('Next.js', scriptConfig, themeConfig, `${componentName}.AiInterviewMeetingWidget`),
                    },
                    {
                        framework: 'Angular',
                        script: generateScript('Angular', scriptConfig, themeConfig, `${componentName}.AiInterviewMeetingWidget`),
                    },
                ],
                title: `Integrate AiInterviewMeetingWidget UI into your framework.`,
                description: `Copy and Paste the following code where you want to launch the ${formattedComponentName} UI`,
                note: 'Make sure main script should available when you execute this code. Update the callback function to set the selected flow ID or perform side effects on flow selection.',
            },
            // AiInterviewScheduleWidget
            {
                script: generateEmbedScript(fileName),
                title: `Embed AiInterviewScheduleWidget into the app.`,
                description: 'Copy and paste the following code in your body tag of html code to inject the script.',
                note: 'Replace path_to_the_file with your file path.',
            },
            {
                script: [
                    {
                        framework: 'HTML',
                        script: generateScript('HTML', scriptConfig, themeConfig, `${componentName}.AiInterviewScheduleWidget`),
                    },
                    {
                        framework: 'React',
                        script: generateScript('React', scriptConfig, themeConfig, `${componentName}.AiInterviewScheduleWidget`),
                    },
                    {
                        framework: 'Next.js',
                        script: generateScript('Next.js', scriptConfig, themeConfig, `${componentName}.AiInterviewScheduleWidget`),
                    },
                    {
                        framework: 'Angular',
                        script: generateScript('Angular', scriptConfig, themeConfig, `${componentName}.AiInterviewScheduleWidget`),
                    },
                ],
                title: `Integrate AiInterviewScheduleWidget UI into your framework.`,
                description: `Copy and Paste the following code where you want to launch the ${formattedComponentName} UI`,
                note: 'Make sure main script should available when you execute this code. Update the callback function to set the selected flow ID or perform side effects on flow selection.',
            },
        ];
        setGeneratedScripts(scripts);
        setIsScriptGenerated(true);
    };
    return {
        generatedScripts,
        handleSubmit,
        fileName,
        fileUrl,
    };
};
